import React from 'react'
import { Anomaly } from '../../types/anomaly'

interface AnomalyDetailModalProps {
  anomaly: Anomaly
  onClose: () => void
}

const AnomalyDetailModal: React.FC<AnomalyDetailModalProps> = ({ anomaly, onClose }) => {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600'
      case 'warning': return 'text-yellow-600'
      case 'info': return 'text-blue-600'
      default: return 'text-gray-600'
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Anomaly Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">ID</label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{anomaly.id}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Timestamp</label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {new Date(anomaly.timestamp).toLocaleString()}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">System ID</label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{anomaly.system_id}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Severity</label>
              <p className={`text-lg font-semibold capitalize ${getSeverityColor(anomaly.severity)}`}>
                {anomaly.severity}
              </p>
            </div>
          </div>

          {/* Metric Details */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Metric Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Metric</label>
                <p className="text-gray-900 dark:text-white">{anomaly.metric}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Anomaly Score</label>
                <p className="text-gray-900 dark:text-white font-mono">{anomaly.score.toFixed(4)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Actual Value</label>
                <p className="text-gray-900 dark:text-white font-mono">{anomaly.value.toFixed(2)}</p>
              </div>
              {anomaly.expected && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Expected Value</label>
                  <p className="text-gray-900 dark:text-white font-mono">{anomaly.expected.toFixed(2)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Features */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Feature Values</h3>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(anomaly.features).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{key}:</span>
                  <span className="text-gray-900 dark:text-white font-mono">{value.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Explanation */}
          {anomaly.explanation && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Feature Contributions</h3>
              <div className="space-y-2">
                {Object.entries(anomaly.explanation).map(([feature, contribution]) => (
                  <div key={feature} className="flex items-center">
                    <span className="w-24 text-sm text-gray-600 dark:text-gray-400">{feature}:</span>
                    <div className="flex-1 mx-2">
                      <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-2 relative">
                        <div
                          className={`absolute h-2 rounded-full ${
                            contribution > 0 ? 'bg-red-500' : 'bg-green-500'
                          }`}
                          style={{
                            width: `${Math.abs(contribution) * 50}%`,
                            left: contribution > 0 ? '50%' : `${50 - Math.abs(contribution) * 50}%`
                          }}
                        />
                        <div className="absolute left-1/2 top-0 w-px h-2 bg-gray-400 dark:bg-gray-500" />
                      </div>
                    </div>
                    <span className={`text-sm font-mono ${contribution > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {contribution > 0 ? '+' : ''}{contribution.toFixed(3)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Context */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Context</h3>
            <pre className="text-sm text-gray-600 dark:text-gray-400 overflow-x-auto">
              {JSON.stringify(anomaly.context, null, 2)}
            </pre>
          </div>

          {/* Acknowledgment Status */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Status</h3>
            {anomaly.acknowledged ? (
              <div className="text-green-600">
                <p>✓ Acknowledged by {anomaly.acknowledged_by}</p>
                <p className="text-sm text-gray-500">
                  {anomaly.acknowledged_at && new Date(anomaly.acknowledged_at).toLocaleString()}
                </p>
              </div>
            ) : (
              <p className="text-red-600">⚠ Pending acknowledgment</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default AnomalyDetailModal